import React from "react";
import { useParams, Link } from "react-router-dom";
import { api, ASSET_URL } from "../lib/api";
import { useSite } from "../lib/site-context";
import { applySeo, injectJsonLd } from "../lib/seo";
import { ArrowLeft } from "lucide-react";

export default function NewsArticlePage() {
  const { slug } = useParams();
  const { lang, t } = useSite();
  const [n, setN] = React.useState(null);
  const [cover, setCover] = React.useState(null);
  const [video, setVideo] = React.useState(null);
  const [poster, setPoster] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.newsBySlug(slug).catch(() => null), api.media().catch(() => []), api.settings().catch(() => ({}))])
      .then(([article, media, settings]) => {
        if (cancelled) return;
        setN(article);
        if (!article) { setCover(null); return; }
        const mm = Object.fromEntries((media || []).map(m => [m.id, m]));
        const base = settings?.public_base_url || process.env.REACT_APP_SITE_URL || "";
        const imgId = article.cover_media_id || article.og_media_id;
        const coverAsset = imgId && mm[imgId] ? mm[imgId] : null;
        setCover(coverAsset);
        setVideo(article.video_media_id && mm[article.video_media_id] ? mm[article.video_media_id] : null);
        setPoster(article.poster_media_id && mm[article.poster_media_id] ? mm[article.poster_media_id] : null);
        const ogImage = coverAsset ? ASSET_URL(coverAsset.variants?.webp || coverAsset.variants?.original || coverAsset.variants?.thumb) : undefined;
        const title = lang === "en" ? (article.title_en || article.title_de) : article.title_de;
        const excerpt = lang === "en" ? (article.excerpt_en || article.excerpt_de) : article.excerpt_de;
        applySeo({
          slug: `wissen/news/${slug}`,
          seo: { title: `${title} — Dr. B.-Digitec`, description: excerpt, keywords: (article.keywords || []).join(", "), og_title: title },
          lang, siteName: settings?.site_name || "Dr. B.-Digitec", baseUrl: base, ogImage,
        });
        // NewsArticle JSON-LD — so AI/search immediately recognise fresh content
        injectJsonLd({
          "@context": "https://schema.org",
          "@graph": [{
            "@type": "NewsArticle",
            headline: title,
            description: excerpt,
            image: ogImage ? [ogImage] : undefined,
            datePublished: article.published_at,
            dateModified: article.updated_at || article.published_at,
            keywords: (article.keywords || []).join(", "),
            inLanguage: lang,
            mainEntityOfPage: `${base}/wissen/news/${slug}${lang === "en" ? "?lang=en" : ""}`,
            author: { "@type": "Organization", name: settings?.site_name || "Dr. B.-Digitec" },
            publisher: { "@type": "Organization", name: settings?.site_name || "Dr. B.-Digitec", logo: { "@type": "ImageObject", url: `${base}/brand/logo.svg` } },
          }],
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; injectJsonLd(null); };
  }, [slug, lang]);

  if (loading) return <div className="pt-40 text-center font-mono text-xs tracking-[0.3em] uppercase opacity-50">Lädt …</div>;
  if (!n) return <div className="pt-40 pb-40 text-center"><h2 className="brut-h2">404 — Beitrag nicht gefunden</h2><Link to="/wissen/news" className="brut-btn mt-6 inline-flex">{t("Zurück", "Back")}</Link></div>;

  const title = lang === "en" ? (n.title_en || n.title_de) : n.title_de;
  const body = lang === "en" ? (n.body_en || n.body_de) : n.body_de;
  const excerpt = lang === "en" ? (n.excerpt_en || n.excerpt_de) : n.excerpt_de;
  const coverSrc = cover ? ASSET_URL(cover.variants?.webp || cover.variants?.original || cover.variants?.thumb) : null;
  const videoSrc = video ? ASSET_URL(video.variants?.original || video.variants?.mp4 || video.variants?.webm || "") : null;
  const posterSrc = poster ? ASSET_URL(poster.variants?.webp || poster.variants?.original || poster.variants?.thumb) : coverSrc;

  return (
    <main className="pt-36 md:pt-44 pb-24" data-testid="news-article">
      <article className="news-article-wrap mx-auto px-5 md:px-8">
        <Link to="/wissen/news" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider opacity-60 hover:opacity-100 mb-8"><ArrowLeft size={14} /> {t("Alle Beiträge", "All articles")}</Link>
        <div className="brut-tag mb-5">{(n.published_at || "").slice(0, 10)}</div>
        <h1 className="news-article-headline brut-h1 mb-6" data-testid="news-article-title">{title}</h1>
        {excerpt && <p className="news-article-excerpt brut-body text-lg md:text-xl opacity-80 mb-10">{excerpt}</p>}

        {coverSrc && (
          <figure className="news-article-hero" data-testid="news-article-cover">
            <img src={coverSrc} alt={title} loading="eager" />
          </figure>
        )}
          {videoSrc && (
            <figure className="news-article-video" data-testid="news-article-video">
              <video controls playsInline preload="metadata" poster={posterSrc || undefined}>
                <source src={videoSrc} type="video/mp4" />
                {t("Ihr Browser kann dieses Video nicht abspielen.", "Your browser cannot play this video.")}
              </video>
            </figure>
          )}


        <div className="news-body rte-content" dangerouslySetInnerHTML={{ __html: body || "" }} />
        {(n.keywords || []).length > 0 && (
          <div className="mt-12 pt-6 border-t-2 border-[var(--ink)]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-50 mb-3">{t("Schlagwörter", "Keywords")}</div>
            <div className="flex flex-wrap gap-2" data-testid="news-keywords">
              {n.keywords.map((k, i) => <span key={i} className="font-mono text-[11px] uppercase bg-[var(--ink)] text-[var(--bg)] px-3 py-1">#{k.replace(/\s+/g, "")}</span>)}
            </div>
          </div>
        )}
      </article>
    </main>
  );
}
